// storybook-check-ignore
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe';
import Box from '@opendoor/bricks/core/Box';
import { useRouter } from 'next/router';

import { useGetFeatureFlag } from 'components/helpers/hooks';
import usePrevious from 'components/landing-pages-v2/utils/usePrevious';

import { useObservability } from '../../../../helpers/observability';
import { COSMOS_URL } from '../../../globals';
import { DEFAULT_NAV_MENU_BUTTON_ID, DEFAULT_NAV_MENU_ID } from '../../../shared/MenuPanel';
import MenuPanel from '../menu/MenuPanel';
import type { NavigationProps } from '../types';
import { AccountSection } from './AccountSection';
import { HeaderMenuButton } from './HeaderMenuButton';
import { LogoSection } from './LogoSection';
import { MegaNav } from './MegaNav';
import { megaNavData } from './megaNavData';
import { NavigationLinks } from './NavigationLinks';
import { SkipLink } from './SkipLink';

const redirectToLogin = () => {
  document.location.href = `${COSMOS_URL}/login?redirectTo=${encodeURIComponent(
    window.location.pathname,
  )}`;
};

const headerHeight = '72px';

const Header = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaTitle,
  ctaLink,
  showLanguageToggle,
}: NavigationProps) => {
  const { pathname } = useRouter();

  const navMenuButtonId = DEFAULT_NAV_MENU_BUTTON_ID;
  const navMenuId = DEFAULT_NAV_MENU_ID;

  const { trackEvent } = useObservability();
  const [isExpanded, setExpanded] = useState(false);
  const { user: human, authentication } = useAuth();

  const isLoggedIn = authentication.state === 'authenticated';

  // Feature flag for mega nav bar
  const [isFeatureFlagLoaded, setIsFeatureFlagLoaded] = useState(false);
  const megaNavFlag = useGetFeatureFlag('growth-25-10-nav-bar', undefined, {
    autoImpression: true,
  });
  const isMegaNavEnabled = megaNavFlag === 'on';

  useEffect(() => {
    if (megaNavFlag) {
      setIsFeatureFlagLoaded(true);
    }
  }, [megaNavFlag]);

  const fullName = human?.fullName;
  const firstName = fullName ? fullName.split(' ')[0] : '';
  const initials = fullName ? fullName.split(' ').map((n) => n[0]) : [];
  const [isVisible, setIsVisible] = useState(false);
  const prevVisible = usePrevious(isVisible);
  const toggleMenuOpen = () => setIsVisible(!isVisible);

  const handleMenuClick = () => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'menu', undefined, { categoryOverride: 'homepage' });
    toggleMenuOpen();
  };

  const handleAccountClick = () => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
    authentication.state === 'authenticated' ? setExpanded(!isExpanded) : redirectToLogin();
  };

  // Hang on to burger button ref for focus management
  const ctaRef = useRef<HTMLButtonElement>(null);

  // When menu close animation is complete
  // and it was previously open.
  // This prevents a focus from the initial animation state
  // on page load.
  const exitComplete = () => {
    if (prevVisible === true) {
      ctaRef.current?.focus();
    }
  };

  //Hide Sign button on the sign in page
  const showLogin = !(pathname.includes('/login') || pathname.includes('/account'));

  // when there are more than two nav links we collapse into menu panel for tablet
  const tabletDisplay = mainNavLinks.length > 2 ? null : 'flex';

  return (
    <>
      <MenuPanel
        slug={slug}
        menuPanelNavLinks={menuPanelNavLinks}
        mainNavLinks={mainNavLinks}
        ctaTitle={ctaTitle}
        ctaLink={ctaLink}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        exitComplete={exitComplete}
        showLanguageToggle={showLanguageToggle}
        isMegaNavEnabled={isMegaNavEnabled}
        megaNavData={megaNavData}
      />

      <Box
        background="white0"
        h={headerHeight}
        position="sticky"
        top="0"
        zIndex="2000"
        pl={[20, null, 0]}
        pr={[20, null, 0]}
      >
        <Box
          display="grid"
          gridTemplateColumns="auto 1fr auto"
          h={headerHeight}
          marginX="auto"
          px={[null, null, 9, 11, 11]}
          maxW="1440px"
        >
          <SkipLink />
          <LogoSection />
          {/* 1 / -1 keeps things centered despite size of left and right columns */}
          {isFeatureFlagLoaded &&
            (isMegaNavEnabled ? (
              <MegaNav slug={slug} />
            ) : (
              <NavigationLinks
                mainNavLinks={mainNavLinks}
                slug={slug}
                tabletDisplay={tabletDisplay}
              />
            ))}
          <Box
            display="flex"
            gridRow="1 / 2"
            gridColumn="-1 / -2"
            alignItems="center"
            gap="24px"
            pl="16px"
          >
            <AccountSection
              isLoggedIn={isLoggedIn}
              showLogin={showLogin}
              firstName={firstName}
              initials={initials}
              isExpanded={isExpanded}
              handleAccountClick={handleAccountClick}
            />
            <Box>
              <HeaderMenuButton
                navMenuId={navMenuId}
                navMenuButtonId={navMenuButtonId}
                isVisible={isVisible}
                handleMenuClick={handleMenuClick}
                ctaRef={ctaRef}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Header;
export type { MenuLink } from '../types';
